import { useEffect, useMemo, useState } from "react";
import { TableContent } from "./TableContent/TableContent";
import styles from "./ArchivedCompaniesPage.module.scss";
import { useQuery } from "@tanstack/react-query";
import { getAllArchivedCompaniesData } from "../model/services/getAllArchivedCompaniesData";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useSearchParams } from "react-router-dom";
import { InnoveraCompanyHeader } from "@/frontend/components/InnoveraCompanyHeader";
import { ArchivedCompaniesHeader } from "./ArchivedCompaniesHeader/ui/ArchivedCompaniesHeader";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { usePipelineFilters } from "@/frontend/components/FilterV2/lib/usePipelineFilters";
import { getAllTags } from "@/frontend/pages/CompaniesPage/model/services/getAllTags";
import { ConditionRowFilterExtended } from "@/frontend/components/FilterV2/model/types";
import clsx from "clsx";
import { Sidebar } from "@/frontend/components/Sidebar/Sidebar";
import { Filters } from "@/frontend/components/FilterV2/ui/Filters";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";

export function ArchivedCompaniesPage() {
  const limit = 10;
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);

  const isMobile = useScreenWidth() <= 768;

  const [areFiltersOpen, setAreFiltersOpen] = useState(false);

  const { filters, setFilters } = usePipelineFilters(
    ELOCAL_STORAGE_KEY.ARCHIVED_COMPANIES_FILTER_DATA,
    "NON_EXISTENT_ARCHIVED"
  );

  const { data: tagsDataResponse } = useQuery({
    queryKey: ["tags-extra"],
    queryFn: async () => {
      const res = await getAllTags();
      return res;
    },
    refetchOnWindowFocus: false,
    gcTime: 0
  });

  const { tagFilters, staticTagFilters, restFilters } = useMemo(() => {
    const tagFilters: ConditionRowFilterExtended[] = [];
    const staticTagFilters: ConditionRowFilterExtended[] = [];
    const restFilters: ConditionRowFilterExtended[] = [];
    filters.forEach((f) => {
      if (f.type === "tag") {
        const correspondingTag = tagsDataResponse?.find((item: any) => item.name === f.value && !item.isStatic);
        if (correspondingTag) {
          tagFilters.push({ ...f, value: correspondingTag.id + "" });
        } else {
          staticTagFilters.push({ ...f, type: "staticTag" });
        }
      } else {
        restFilters.push(f);
      }
    });
    return { tagFilters, staticTagFilters, restFilters };
  }, [filters, tagsDataResponse]);

  const {
    data: allCompaniesData,
    isLoading: isLoadingAll,
    refetch: refetchAll
  } = useQuery({
    queryKey: ["allArchivedCompaniesData", page, tagFilters, staticTagFilters, restFilters],
    queryFn: async () => {
      return getAllArchivedCompaniesData({
        limit: limit,
        page: page,
        filters: [...tagFilters, ...staticTagFilters, ...restFilters]
      });
    },
    gcTime: 0,
    throwOnError: (error) => {
      console.error(error);
      snackbarConstructor("Error fetching companies", "error");
      reportErrorToSentry(
        error,
        "DASHBOARD: /portfolio-companies?page=${page}&limit=${limit} || /portfolio-companies?page=${page}&limit=${limit} "
      );
      return false;
    }
  });

  function handleCardChange(id: number) {
    setSelectedRowIds((currentSelected) => {
      if (!currentSelected.includes(id)) {
        return [...currentSelected, id];
      }
      return currentSelected.filter((cardId) => cardId !== id);
    });
  }

  function handleChangeAllCards() {
    setSelectedRowIds(allCompaniesData?.portfolioCompanies.map((item: { id: number }) => item.id) || []);

    if (selectedRowIds.length > 0) {
      setSelectedRowIds([]);
    }
  }

  function triggerRefetch() {
    refetchAll();
    setSelectedRowIds([]);
  }

  useEffect(() => {
    searchParams.set("page", page.toString());
    setSearchParams(searchParams, { replace: true });
  }, [page, searchParams, setSearchParams]);

  useEffect(() => {
    if (page === 1) return;
    if (allCompaniesData?.portfolioCompanies?.length === 0) setPage((p) => p - 1);
  }, [allCompaniesData]);

  return (
    <div className={styles.headerContainer}>
      <InnoveraCompanyHeader className={styles.innoveraHeader} />
      <div className={styles.pageContainer}>
        <div className={clsx(styles.sidebarContainer, areFiltersOpen && !isMobile && styles.open)}>
          <Sidebar isOpen={areFiltersOpen} onClose={() => setAreFiltersOpen(false)} isMobile={isMobile}>
            <div className={styles.borderRight}>
              <Filters
                filters={filters}
                onCancel={() => setAreFiltersOpen(false)}
                onSave={(data) => {
                  setFilters(data);
                }}
                options={{
                  tag:
                    tagsDataResponse?.map((t: any) => ({
                      label: t.name,
                      value: t.name
                    })) || []
                }}
                wrap
                noControls
              />
            </div>
          </Sidebar>
        </div>

        <div className={styles.contentContainer}>
          <ArchivedCompaniesHeader
            className={styles.dashboardHeader}
            selected={selectedRowIds}
            onChange={handleChangeAllCards}
            triggerRefetch={triggerRefetch}
            allCompaniesData={allCompaniesData}
            setAreFiltersOpen={setAreFiltersOpen}
            numFilters={filters.length}
            areFiltersOpen={areFiltersOpen}
          />
          {isLoadingAll ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : (
            <div className={styles.content}>
              <TableContent
                handleCardChange={handleCardChange}
                handleChangeAllCards={handleChangeAllCards}
                selectedRowIds={selectedRowIds}
                data={allCompaniesData}
                triggerRefetch={triggerRefetch}
                limit={limit}
                setPage={setPage}
                currentPage={page}
                isLoading={isLoadingAll}
                className={styles.portfolioTableContent}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
